import { useCallback, useEffect, useRef, useState } from 'react';
import type { DrillArrow, NormPoint } from '@4cm/core/drills/types';
import { pitchViewBox, pitchBackgroundRect, drillNormToPitchSvg, pitchSvgToDrillNorm, visibleRegion, type PitchView, type CustomPitch } from './pitchArea';
import type { PitchSpec } from './pitchSpecs';
import { toolForMode, type DrawingMode } from './drawingTypes';

interface DrawingOverlay2DProps {
  arrows: DrillArrow[];
  onChange: (next: DrillArrow[]) => void;
  /** Currently armed draw mode. null = idle (overlay is click-through). */
  activeMode: DrawingMode | null;
  spec: PitchSpec;
  view: PitchView;
  custom?: CustomPitch | null;
}

/**
 * SVG overlay that captures click+drag to draw arrows of different
 * semantic kinds (pass / run / dribble / press / cover) + an erase
 * mode. Each kind has its own visual style. Arrows persist into
 * drill_spec.arrows.
 *
 *   - Click on empty pitch + drag → preview arrow follows the pointer
 *   - Release → arrow committed at the (drag-start, drag-end) endpoints
 *   - Erase mode + click on an existing arrow → arrow removed
 *   - Idle mode → overlay is click-through to whatever's beneath
 */
export function DrawingOverlay2D({ arrows, onChange, activeMode, spec, view, custom }: DrawingOverlay2DProps): React.JSX.Element {
  const svgRef = useRef<SVGSVGElement>(null);
  const [draftStart, setDraftStart] = useState<NormPoint | null>(null);
  const [draftEnd, setDraftEnd] = useState<NormPoint | null>(null);
  // Arrow editing in Select mode: click an arrow to select it; drag
  // an endpoint handle to reposition; Delete key to remove.
  const [selectedArrowId, setSelectedArrowId] = useState<string | null>(null);
  const [dragEndpoint, setDragEndpoint] = useState<{ arrowId: string; which: 'from' | 'to' } | null>(null);

  const tool = toolForMode(activeMode);
  const isErase = activeMode === 'erase';
  const isDrawing = !!tool && !isErase;

  const eventToNorm = useCallback((evt: React.PointerEvent<SVGSVGElement>): NormPoint | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const pt = svg.createSVGPoint();
    pt.x = evt.clientX;
    pt.y = evt.clientY;
    const ctm = svg.getScreenCTM();
    if (!ctm) return null;
    const local = pt.matrixTransform(ctm.inverse());
    return pitchSvgToDrillNorm(local.x, local.y, spec, view, custom);
  }, [spec, view, custom]);

  const handlePointerDown = useCallback((evt: React.PointerEvent<SVGSVGElement>) => {
    if (!activeMode) return;
    // Erase handled via per-arrow handler below.
    if (isErase) return;
    if ((evt.target as Element).getAttribute('data-draw-bg') !== '1') return;
    const norm = eventToNorm(evt);
    if (!norm) return;
    setDraftStart(norm);
    setDraftEnd(norm);
    (evt.currentTarget as Element).setPointerCapture?.(evt.pointerId);
  }, [activeMode, isErase, eventToNorm]);

  const handlePointerMove = useCallback((evt: React.PointerEvent<SVGSVGElement>) => {
    if (dragEndpoint) {
      const norm = eventToNorm(evt);
      if (!norm) return;
      const point = { kind: 'point' as const, position: norm };
      onChange(arrows.map((a) =>
        a.id === dragEndpoint.arrowId
          ? { ...a, [dragEndpoint.which]: point }
          : a,
      ));
      return;
    }
    if (!draftStart) return;
    const norm = eventToNorm(evt);
    if (!norm) return;
    setDraftEnd(norm);
  }, [draftStart, dragEndpoint, arrows, eventToNorm, onChange]);

  const handlePointerUp = useCallback(() => {
    if (dragEndpoint) {
      setDragEndpoint(null);
      return;
    }
    if (!draftStart || !draftEnd || !tool || isErase) {
      setDraftStart(null);
      setDraftEnd(null);
      return;
    }
    // Discard if the user barely moved — almost certainly a stray click.
    const dx = draftEnd[0] - draftStart[0];
    const dy = draftEnd[1] - draftStart[1];
    if (Math.hypot(dx, dy) < 0.02) {
      setDraftStart(null);
      setDraftEnd(null);
      return;
    }
    const arrow: DrillArrow = {
      id: `arr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`,
      from: { kind: 'point', position: draftStart },
      to:   { kind: 'point', position: draftEnd },
      kind: tool.id === 'erase' ? undefined : tool.id,
      style: tool.style,
      curvature: tool.curvature || undefined,
      color: tool.color,
    };
    onChange([...arrows, arrow]);
    setDraftStart(null);
    setDraftEnd(null);
  }, [draftStart, draftEnd, tool, isErase, arrows, onChange]);

  const handleArrowClick = useCallback((id: string) => (evt: React.PointerEvent<SVGGElement>) => {
    evt.stopPropagation();
    if (isErase) {
      onChange(arrows.filter((a) => a.id !== id));
      return;
    }
    // Select mode: tap to select; tap again to deselect.
    if (!activeMode) {
      setSelectedArrowId((prev) => (prev === id ? null : id));
    }
  }, [isErase, activeMode, arrows, onChange]);

  // Endpoint drag — pointerdown on a handle starts the drag; the SVG's
  // pointermove + pointerup finish it.
  const handleHandleDown = useCallback((arrowId: string, which: 'from' | 'to') => (evt: React.PointerEvent<SVGCircleElement>) => {
    evt.stopPropagation();
    setDragEndpoint({ arrowId, which });
    (evt.currentTarget as Element).setPointerCapture?.(evt.pointerId);
  }, []);

  // Keyboard: Delete / Backspace removes the selected arrow.
  useEffect(() => {
    if (!selectedArrowId) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Delete' || e.key === 'Backspace') {
        // Only react when no input/textarea has focus.
        const tag = (document.activeElement?.tagName ?? '').toLowerCase();
        if (tag === 'input' || tag === 'textarea') return;
        onChange(arrows.filter((a) => a.id !== selectedArrowId));
        setSelectedArrowId(null);
      } else if (e.key === 'Escape') {
        setSelectedArrowId(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedArrowId, arrows, onChange]);

  // Show an in-progress drag preview when the coach is mid-stroke.
  const draftArrow: DrillArrow | null = (draftStart && draftEnd && tool && !isErase)
    ? {
        id: '__draft__',
        from: { kind: 'point', position: draftStart },
        to:   { kind: 'point', position: draftEnd },
        kind: tool.id === 'erase' ? undefined : tool.id,
        style: tool.style,
        curvature: tool.curvature || undefined,
        color: tool.color,
      }
    : null;

  // SVG always listens. The background <rect> only swallows clicks when
  // a draw mode is armed. Note: must be 'all' (not 'auto') because the
  // rect has fill="transparent" — under SVG's default visiblePainted
  // semantics, a transparent fill isn't a hit target. 'all' overrides.
  const bgPointerEvents = activeMode ? 'all' : 'none';

  // Stroke + arrowhead size scale with the visible region. Subtle by
  // default — arrows should READ as the play, not dominate the canvas.
  const region = visibleRegion(spec, view, custom);
  const regionLength = region.length;
  const arrowStroke = Math.max(0.06, region.width * 0.006);

  return (
    <svg
      ref={svgRef}
      viewBox={pitchViewBox(spec, view, custom)}
      preserveAspectRatio="xMidYMid meet"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
      style={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        // SVG element itself is click-through when idle — clicks fall
        // through to overlays beneath. Children (bg rect, arrow groups)
        // independently opt back in when they need to capture.
        pointerEvents: activeMode || draftStart || dragEndpoint ? 'auto' : 'none',
      }}
      aria-label="Drill drawing overlay"
    >
      <rect
        data-draw-bg="1"
        {...pitchBackgroundRect(spec, view, custom)}
        fill="transparent"
        style={{
          cursor: isDrawing ? 'crosshair' : isErase ? 'not-allowed' : 'default',
          pointerEvents: bgPointerEvents,
        }}
      />
      {arrows.map((a) => (
        <ArrowPath
          key={a.id}
          arrow={a}
          spec={spec}
          view={view}
          custom={custom}
          sw={arrowStroke}
          regionLength={regionLength}
          selected={selectedArrowId === a.id}
          onPointerDown={handleArrowClick(a.id)}
          hoverable={isErase || !activeMode}
        />
      ))}
      {draftArrow && <ArrowPath arrow={draftArrow} spec={spec} view={view} custom={custom} sw={arrowStroke} regionLength={regionLength} preview />}
      {/* Endpoint handles for the currently selected arrow. */}
      {selectedArrowId && (() => {
        const a = arrows.find((x) => x.id === selectedArrowId);
        if (!a) return null;
        const fromP = a.from.kind === 'point' ? a.from.position : [0.5, 0.5] as NormPoint;
        const toP   = a.to.kind   === 'point' ? a.to.position   : [0.5, 0.5] as NormPoint;
        const fromS = drillNormToPitchSvg(fromP, spec, view, custom);
        const toS   = drillNormToPitchSvg(toP,   spec, view, custom);
        const r = Math.max(0.3, region.width * 0.025);
        return (
          <g>
            <circle
              cx={fromS.cx} cy={fromS.cy} r={r}
              fill="#27AE60" stroke="#fff" strokeWidth={r * 0.18}
              style={{ cursor: 'grab', pointerEvents: 'auto' }}
              onPointerDown={handleHandleDown(a.id, 'from')}
            />
            <circle
              cx={toS.cx} cy={toS.cy} r={r}
              fill="#27AE60" stroke="#fff" strokeWidth={r * 0.18}
              style={{ cursor: 'grab', pointerEvents: 'auto' }}
              onPointerDown={handleHandleDown(a.id, 'to')}
            />
          </g>
        );
      })()}
    </svg>
  );
}

interface ArrowPathProps {
  arrow: DrillArrow;
  spec: PitchSpec;
  view: PitchView;
  custom?: CustomPitch | null;
  /** Stroke width in viewBox metres — sized to the visible region by the caller. */
  sw: number;
  /** Visible region length — used to size the arrowhead. */
  regionLength: number;
  selected?: boolean;
  preview?: boolean;
  hoverable?: boolean;
  onPointerDown?: (evt: React.PointerEvent<SVGGElement>) => void;
}

function ArrowPath({ arrow, spec, view, custom, sw, regionLength, selected, preview, hoverable, onPointerDown }: ArrowPathProps): React.JSX.Element {
  const from = arrow.from.kind === 'point' ? arrow.from.position : [0.5, 0.5] as NormPoint;
  const to   = arrow.to.kind   === 'point' ? arrow.to.position   : [0.5, 0.5] as NormPoint;
  const start = drillNormToPitchSvg(from, spec, view, custom);
  const end   = drillNormToPitchSvg(to, spec, view, custom);
  const color = arrow.color ?? '#27AE60';
  const dash = arrow.style === 'dashed' ? `${sw * 3.5} ${sw * 2.2}`
             : arrow.style === 'dotted' ? `${sw * 0.5} ${sw * 1.6}`
             : undefined;
  const curvature = arrow.curvature ?? 0;

  // Arrowhead: render inline as a small triangle pointing along the
  // tangent at the path end. Sized to the visible region so a 15×15 m
  // grid and a full pitch both render legible heads.
  const headSize = Math.max(0.22, regionLength * 0.012);
  const angle = curvature > 0
    ? Math.atan2(end.cy - midControl(start, end, curvature).cy, end.cx - midControl(start, end, curvature).cx)
    : Math.atan2(end.cy - start.cy, end.cx - start.cx);
  // Shorten the line so the stroke ends BEFORE the arrowhead tip —
  // otherwise the rounded line cap pokes through the triangle.
  const shorten = headSize * 0.55;
  const endShort = { cx: end.cx - Math.cos(angle) * shorten, cy: end.cy - Math.sin(angle) * shorten };
  const d = curvature > 0 ? buildCurvedPath(start, endShort, curvature) : buildStraightPath(start, endShort);
  const headTri = buildArrowheadTri(end, angle, headSize);

  // Midpoint for an optional sequence-number label.
  const labelPoint = curvature > 0
    ? midControl(start, end, curvature)
    : { cx: (start.cx + end.cx) / 2, cy: (start.cy + end.cy) / 2 };

  return (
    <g
      onPointerDown={onPointerDown}
      style={{
        cursor: hoverable ? 'pointer' : 'default',
        opacity: preview ? 0.7 : 1,
        // When a draw mode is armed (hoverable=false), existing arrows
        // must be CLICK-THROUGH so a new drag lands on the bg rect
        // instead of the arrow's wider invisible hit area. Idle +
        // Erase modes need the arrow to be clickable.
        pointerEvents: hoverable ? 'auto' : 'none',
      }}
    >
      {/* Invisible wider hit-area so the arrow is easy to click. */}
      <path
        d={d}
        fill="none"
        stroke="transparent"
        strokeWidth={Math.max(sw * 5, headSize * 0.8)}
        strokeLinecap="round"
      />
      {/* Selection halo. */}
      {selected && (
        <path
          d={d}
          fill="none"
          stroke="#27AE60"
          strokeWidth={sw * 2.8}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.35}
        />
      )}
      {/* Visible stroke. */}
      <path
        d={d}
        fill="none"
        stroke={color}
        strokeWidth={sw}
        strokeDasharray={dash}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d={headTri}
        fill={color}
        stroke={color}
        strokeWidth={sw * 0.4}
        strokeLinejoin="round"
      />
      {/* Optional sequence label rendered at the arrow midpoint. */}
      {arrow.label && (
        <g pointerEvents="none">
          <circle
            cx={labelPoint.cx} cy={labelPoint.cy}
            r={headSize * 0.95}
            fill="rgba(0, 0, 0, 0.75)"
            stroke={color}
            strokeWidth={sw * 0.6}
          />
          <text
            x={labelPoint.cx} y={labelPoint.cy + headSize * 0.4}
            textAnchor="middle"
            fontSize={headSize * 1.1}
            fontWeight={700}
            fill="#fff"
          >
            {arrow.label}
          </text>
        </g>
      )}
    </g>
  );
}

function midControl(a: { cx: number; cy: number }, b: { cx: number; cy: number }, curvature: number): { cx: number; cy: number } {
  const mx = (a.cx + b.cx) / 2;
  const my = (a.cy + b.cy) / 2;
  const dx = b.cx - a.cx;
  const dy = b.cy - a.cy;
  const len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len;
  const ny =  dx / len;
  const offset = curvature * len * 0.4;
  return { cx: mx + nx * offset, cy: my + ny * offset };
}

function buildArrowheadTri(tip: { cx: number; cy: number }, angle: number, size: number): string {
  // Triangle with the tip AT (cx, cy) pointing along `angle`. Base is
  // size×0.7 wide, perpendicular to `angle`, set back by `size`.
  const back = size;
  const wide = size * 0.55;
  const bx = tip.cx - Math.cos(angle) * back;
  const by = tip.cy - Math.sin(angle) * back;
  // Perpendicular vector
  const px = -Math.sin(angle) * wide;
  const py =  Math.cos(angle) * wide;
  const l = { cx: bx + px, cy: by + py };
  const r = { cx: bx - px, cy: by - py };
  return `M ${tip.cx} ${tip.cy} L ${l.cx} ${l.cy} L ${r.cx} ${r.cy} Z`;
}

function buildStraightPath(a: { cx: number; cy: number }, b: { cx: number; cy: number }): string {
  return `M ${a.cx} ${a.cy} L ${b.cx} ${b.cy}`;
}

function buildCurvedPath(a: { cx: number; cy: number }, b: { cx: number; cy: number }, curvature: number): string {
  // Quadratic bezier whose control point sits perpendicular to the
  // chord midpoint. Curvature 0..1 maps to ~25% of the chord length.
  const mx = (a.cx + b.cx) / 2;
  const my = (a.cy + b.cy) / 2;
  const dx = b.cx - a.cx;
  const dy = b.cy - a.cy;
  const len = Math.hypot(dx, dy) || 1;
  // Perpendicular unit vector (rotated +90°).
  const nx = -dy / len;
  const ny =  dx / len;
  const offset = curvature * len * 0.4;
  const cx = mx + nx * offset;
  const cy = my + ny * offset;
  return `M ${a.cx} ${a.cy} Q ${cx} ${cy} ${b.cx} ${b.cy}`;
}
